/*
 * sp-custom-giveaway-product.js
 * ------------------------------------------------------------
 * Powers the Custom Giveaway Product section (sp-custom-giveaway-product.liquid).
 *
 * Responsibilities:
 *   1. Buy button -> clear cart, add the selected variant, go to checkout.
 *      Bound via event delegation on `document` so it keeps working after the
 *      buy area is swapped in by the countdown reveal (inline scripts injected
 *      through innerHTML never execute, delegated listeners do).
 *   2. Sticky submit button (IntersectionObserver) shown when the real button
 *      scrolls out of view.
 *   3. Optional pre-drop countdown. The reveal is SERVER-authoritative: when the
 *      timer hits zero we poll the Section Rendering API (cache-busted) and only
 *      swap in the buy area once the freshly rendered section reports it is live.
 *      Nothing is hidden client-side, so it cannot be unlocked from dev tools.
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var SECTION_CLASS = 'section-custom-giveaway-product';
  var SUBMIT_SELECTOR = '.' + SECTION_CLASS + ' .product-form__submit';

  /* -------------------------------------------------- *
   * 1. Buy button -> clear cart, add variant, checkout *
   * -------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest(SUBMIT_SELECTOR);
    if (!button || button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

    var root = button.closest('product-info') || document;
    var checked = root.querySelector('input[name="variant_id"]:checked');
    if (!checked) return;

    setButtonLoading(button, true);

    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: checked.value, quantity: 1 }] }),
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // /cart/add.js returns a `status` field only on failure (e.g. sold out).
        if (data && data.status) {
          setButtonSoldOut(button);
          return;
        }
        window.location.href = '/checkout';
      })
      .catch(function (error) {
        console.error('Giveaway add-to-cart failed:', error);
        setButtonLoading(button, false);
      });
  });

  function setButtonLoading(button, isLoading) {
    button.classList.toggle('loading', isLoading);
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  function setButtonSoldOut(button) {
    setButtonLoading(button, false);
    button.setAttribute('disabled', 'disabled');
    var label = button.querySelector('span');
    if (label) label.textContent = window.Shopify && window.Shopify.strings ? window.Shopify.strings.soldOut || 'Sold out' : 'Sold out';
  }

  /* -------------------------------------------------- *
   * 2. Sticky submit button                            *
   * -------------------------------------------------- */
  function initStickyButton(productInfo) {
    var button = productInfo.querySelector('.product-form__submit');
    if (!button || button.dataset.stickyBound === 'true') return;
    button.dataset.stickyBound = 'true';

    var stickyId = 'ProductSubmitButtonSticky-' + productInfo.dataset.section;
    var wasInView = false;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var sticky = document.getElementById(stickyId);
          if (entry.isIntersecting) {
            wasInView = true;
            if (sticky) sticky.remove();
          } else {
            if (wasInView && entry.boundingClientRect.top < 0 && !sticky) {
              sticky = document.createElement('button');
              sticky.id = stickyId;
              sticky.className = 'button button--full-width button--primary button--sticky';
              var label = button.querySelector('span');
              sticky.innerHTML = '<span>' + (label ? label.innerHTML : '') + '</span>';
              document.body.appendChild(sticky);
              sticky.addEventListener('click', function () {
                button.scrollIntoView({ behavior: 'smooth', block: 'end' });
              });
            }
            wasInView = false;
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(button);
  }

  /* -------------------------------------------------- *
   * 3. Countdown + server-confirmed reveal             *
   * -------------------------------------------------- */
  function initGiveaway(productInfo) {
    // Countdown off -> legacy behaviour, just wire up the sticky button.
    if (productInfo.dataset.giveawayCountdown !== 'true') {
      initStickyButton(productInfo);
      return;
    }

    // Already live at page load (server rendered the buy area).
    if (productInfo.dataset.giveawayState === 'live') {
      initStickyButton(productInfo);
      return;
    }

    var dropTs = parseInt(productInfo.dataset.giveawayDropTs, 10);
    // Missing/blank drop time -> server already renders live, nothing to gate.
    if (!dropTs || isNaN(dropTs)) return;

    var timer = productInfo.querySelector('[data-giveaway-timer]');
    var interval = window.setInterval(tick, 1000);
    tick();

    function tick() {
      var remaining = dropTs - Math.floor(Date.now() / 1000);
      if (remaining > 2) {
        renderClock(timer, remaining);
      } else {
        window.clearInterval(interval);
        renderWaiting(timer);
        pollForReveal(productInfo);
      }
    }
  }

  function renderClock(timer, remaining) {
    if (!timer) return;
    var days = Math.floor(remaining / 86400);
    var hours = Math.floor((remaining % 86400) / 3600);
    var mins = Math.floor((remaining % 3600) / 60);
    var secs = remaining % 60;
    // Drop each leading group once it (and everything larger) reaches zero, so
    // the final minute shows only minutes/seconds and the last minute only seconds.
    setUnit(timer, '[data-cd-days]', days, days > 0);
    setUnit(timer, '[data-cd-hours]', hours, days > 0 || hours > 0);
    setUnit(timer, '[data-cd-mins]', mins, days > 0 || hours > 0 || mins > 0);
    setUnit(timer, '[data-cd-secs]', secs, true);
  }

  function setUnit(timer, selector, value, visible) {
    var el = timer.querySelector(selector);
    if (!el) return;
    el.textContent = value < 10 ? '0' + value : String(value);
    var unit = el.closest('.giveaway-countdown__unit');
    if (unit) unit.classList.toggle('giveaway-countdown__unit--hidden', !visible);
  }

  function renderWaiting(timer) {
    if (!timer) return;
    timer.classList.add('giveaway-countdown--waiting');
    var clock = timer.querySelector('.giveaway-countdown__clock');
    if (clock) clock.setAttribute('aria-busy', 'true');
  }

  function pollForReveal(productInfo) {
    var sectionId = productInfo.dataset.section;
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&t=' + Date.now();

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, cache: 'no-store' })
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        var doc = new DOMParser().parseFromString(responseText, 'text/html');
        var fresh = doc.querySelector('product-info');
        if (fresh && fresh.dataset.giveawayState === 'live') {
          revealBuyArea(productInfo, doc);
        } else {
          retryPoll(productInfo);
        }
      })
      .catch(function () {
        retryPoll(productInfo);
      });
  }

  function retryPoll(productInfo) {
    // Jitter (3-5s) so thousands of tabs don't stampede the server at once.
    window.setTimeout(function () {
      pollForReveal(productInfo);
    }, 3000 + Math.floor(Math.random() * 2000));
  }

  function revealBuyArea(productInfo, freshDoc) {
    var id = 'ProductInfo-' + productInfo.dataset.section;
    var freshInfo = freshDoc.getElementById(id);
    var liveInfo = document.getElementById(id);
    if (freshInfo && liveInfo) {
      liveInfo.innerHTML = freshInfo.innerHTML;
    }
    productInfo.dataset.giveawayState = 'live';
    initStickyButton(productInfo);
    document.dispatchEvent(
      new CustomEvent('giveaway:live', { detail: { section: productInfo.dataset.section } })
    );
  }

  /* -------------------------------------------------- *
   * Boot                                               *
   * -------------------------------------------------- */
  function boot() {
    document.querySelectorAll('product-info[data-giveaway-countdown]').forEach(initGiveaway);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
